/**
 * HeaderStats — a compact row of labelled figures for a widget or section header.
 *
 * A collapsible report section keeps a few headline figures in its header so a
 * collapsed page still reads as a report (the Measurement report pages collapse
 * their detailed sections by default). Widgets build the items from their own
 * data and pass them to `Widget` as `headerSummary`; the host renders a
 * section's summary widget the same way. Figures are formatted here so every
 * header reads alike.
 */
import { Skeleton } from "@nexus/ui";
import type { ReactNode } from "react";

export type HeaderStatFormat = "number" | "compact" | "percent" | "decimal" | "text";

export interface HeaderStat {
  /** Stable key; defaults to the label. */
  id?: string;
  label: string;
  /** `null` renders an em dash (the source could not compute it). */
  value: number | string | null;
  /** How a number is written; ignored for strings. Defaults to "compact". */
  format?: HeaderStatFormat;
  /** Unit; a currency symbol (€, $, £) is written before the figure. */
  unit?: string | null;
  tone?: "default" | "ok" | "warn" | "error";
  /** Extra content under the value, e.g. a sparkline or a bar. */
  detail?: ReactNode;
  /** The figure is not known yet: a placeholder holds its place. */
  loading?: boolean;
}

const CURRENCY_SYMBOLS = new Set(["€", "$", "£"]);

const FORMATTERS: Record<Exclude<HeaderStatFormat, "text">, Intl.NumberFormat> = {
  number: new Intl.NumberFormat("en", { maximumFractionDigits: 0 }),
  compact: new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }),
  percent: new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 }),
  decimal: new Intl.NumberFormat("en", { maximumFractionDigits: 1 }),
};

/** One figure as written in a header: compact by default, currency before the value. */
export function formatStatValue(
  value: number | string | null,
  format: HeaderStatFormat = "compact",
  unit?: string | null,
): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || format === "text") return String(value);
  if (!Number.isFinite(value)) return "—";
  const formatted = FORMATTERS[format].format(value);
  if (!unit) return formatted;
  return CURRENCY_SYMBOLS.has(unit) ? `${unit}${formatted}` : `${formatted} ${unit}`;
}

const TONE_CLASS: Record<NonNullable<HeaderStat["tone"]>, string> = {
  default: "text-primary",
  ok: "text-status-ok-text",
  warn: "text-status-warn-text",
  error: "text-status-error-text",
};

export function HeaderStats({
  items,
  className = "",
  label,
}: {
  items: readonly HeaderStat[];
  className?: string;
  /** Accessible name of the group, e.g. "Pirate audience summary". */
  label?: string;
}) {
  if (items.length === 0) return null;
  return (
    <dl
      aria-label={label}
      className={`flex min-w-0 flex-wrap items-center justify-end gap-x-5 gap-y-1 ${className}`}
    >
      {items.map((item) => (
        <div key={item.id ?? item.label} className="flex min-w-16 flex-col items-center text-center">
          <dt className="whitespace-nowrap text-2xs font-medium text-muted">{item.label}</dt>
          <dd
            className={`text-sm font-semibold tabular-nums ${TONE_CLASS[item.tone ?? "default"]}`}
          >
            {item.loading ? (
              <Skeleton className="my-0.5 h-4 w-10" />
            ) : (
              formatStatValue(item.value, item.format, item.unit)
            )}
          </dd>
          {item.detail ? <dd className="mt-0.5">{item.detail}</dd> : null}
        </div>
      ))}
    </dl>
  );
}
